import React, { useState, useEffect } from 'react';
import { useActiveSection, useScrollY } from '../hooks';
import Button from './Button';

const NAV_ITEMS = [
  { label: 'Home', href: '#hero' },
  { label: 'About Us', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Areas', href: '#areas' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Reviews', href: '#reviews' },
];

const SECTIONS = ['hero', 'about', 'services', 'areas', 'gallery', 'reviews', 'contact'];

export default function Navbar() {
  const active = useActiveSection(SECTIONS);
  const scrollY = useScrollY();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const elevated = scrollY > 20;

  return (
    <div style={{ width: '100%', overflowX: 'hidden' }}>
      <style>{`

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .nav-logo {
          height: 300px;
          width: auto;
          max-width: 200px;
          object-fit: contain;
          display: block;
        }

        /* Tablet and small screens */
        @media (max-width: 768px) {
          .nav-logo {
            height: 200px;
            max-width: 10x;
          }

          .desktop-nav {
            display: none !important;
          }

          .hamburger {
            display: flex !important;
          }
        }

        /* Phones smaller than iPhone X */
        @media (max-width: 375px) {
          .nav-logo {
            height: 300px;
            max-width: 100px;
          }
        }

        /* Very small phones */
        @media (max-width: 340px) {
          .nav-logo {
            max-width: 110px;
          }
        }

        .nav-link-item {
          position: relative;
          padding-bottom: 3px;
        }

        .nav-link-item::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--orange);
          border-radius: 2px;
          transform: scaleX(0);
          transition: transform 0.25s ease;
        }

        .nav-link-item:hover::after,
        .nav-link-item.active-link::after {
          transform: scaleX(1);
        }

        .mobile-menu-item:hover {
          background: rgba(255,255,255,0.08) !important;
        }

      `}</style>

      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 4%',
          height: 72,
          background: 'rgba(15,35,64,0.97)',
          backdropFilter: 'blur(14px)',
          boxShadow: elevated
            ? '0 2px 30px rgba(0,0,0,0.25)'
            : '0 1px 0 rgba(255,255,255,0.06)',
          transition: 'all 0.3s',
        }}
      >
        <a href="#hero" style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <img src="/images/logo.png" alt="Logo" className="nav-logo" />
        </a>

        <ul
          className="desktop-nav"
          style={{
            display: 'flex',
            gap: 28,
            listStyle: 'none',
            alignItems: 'center',
            margin: 0,
            padding: 0,
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.href.replace('#', '');
            return (
              <li key={item.href}>
                <a
                  href={item.href}
                  className={'nav-link-item' + (isActive ? ' active-link' : '')}
                  style={{
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.72)',
                    textDecoration: 'none',
                    fontSize: '0.88rem',
                    fontWeight: isActive ? 600 : 500,
                  }}
                >
                  {item.label}
                </a>
              </li>
            );
          })}

          <li>
            <Button
              href="#contact"
              variant="primary"
              style={{ padding: '9px 20px', fontSize: '0.85rem' }}
            >
              Contact Us
            </Button>
          </li>
        </ul>

        <button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#fff',
            fontSize: 28,
          }}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </nav>

      {menuOpen && (
        <div
          className="mobile-menu"
          style={{
            position: 'fixed',
            top: 72,
            left: 0,
            right: 0,
            zIndex: 199,
            maxHeight: 'calc(100vh - 72px)',
            overflowY: 'auto',
            background: 'rgba(15,35,64,0.98)',
            backdropFilter: 'blur(14px)',
            padding: '12px 5% 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            animation: 'slideDown 0.2s ease',
          }}
        >
          {NAV_ITEMS.concat([{ label: 'Contact Us', href: '#contact' }]).map(
            (item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="mobile-menu-item"
                style={{
                  color: '#fff',
                  textDecoration: 'none',
                  padding: '14px 16px',
                  borderRadius: 8,
                  display: 'block',
                }}
              >
                {item.label}
              </a>
            )
          )}

          <div
            style={{
              marginTop: 12,
              paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              gap: 10,
            }}
          >
            <a
              href="tel:+447853481188"
              style={{
                flex: 1,
                textAlign: 'center',
                background: 'var(--orange)',
                color: '#fff',
                borderRadius: 8,
                padding: '12px',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Call
            </a>

            <a
              href="https://wa.me"
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1,
                textAlign: 'center',
                background: '#25d366',
                color: '#fff',
                borderRadius: 8,
                padding: '12px',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}